import type {
    AnimatedIconTransitionFramesConstantType,
    AnimatedIconTransitionTimingConstantType,
    AnimatedIconTransitionPartsConstantType,
    AnimatedIconTransitionPlanConstantType,
    AnimatedIconSharedIndexesConstantType,
    AnimatedIconMotionFramesConstantType,
    AnimatedIconPartFramesConstantType,
    AnimatedIconPartRangesConstantType,
    AnimatedIconRangeValueConstantType,
    AnimatedIconPartsKindConstantType,
    AnimatedIconRangeConstantType,
    AnimatedIconPlanConstantType,
    AnimatedIconTransitionTiming,
    AnimatedIconTransitionPart,
    AnimatedIconTransitionPlan,
    AnimatedIconSharedIndexes,
    AnimatedIconPartProperty,
    AnimatedIconPartRanges,
    AnimatedIconPartsKind,
    AnimatedIconFrame,
    AnimatedIconRange,
    AnimatedIconPart,
    AnimatedIconPlan,
    AnimatedIconNode,
    AnimatedIconTime
} from "./type";
import {
    ANIMATED_ICON_TRACE_STAGGER_SHARE,
    ANIMATED_ICON_EASING_SAMPLE_COUNT,
    ANIMATED_ICON_TRANSITION_DURATION,
    ANIMATED_ICON_SIGNATURE_DURATION,
    ANIMATED_ICON_TRACE_DURATION,
    ANIMATED_ICON_DRAW_DURATION,
    ANIMATED_ICON_DRAW_STAGGER,
    ANIMATED_ICON_TRACE_PHASES,
    ANIMATED_ICON_STEP_OFFSET,
    ANIMATED_ICON_EASINGS,
    ANIMATED_ICON_MOTIONS,
    ANIMATED_ICON_CENTER
} from "./constant";
import {
    getAnimatedIconName
} from "./nodes";
import {
    ANIMATED_ICON_SIGNATURES
} from "./signature";
import {
    ANIMATED_ICON_REDUCED_TRANSITION,
    ANIMATED_ICON_SWAP_TRANSITION,
    ANIMATED_ICON_TRANSITIONS
} from "./transition";
import {
    warnOnce
} from "../../utils/development";

const PART_PROPERTIES: Array<AnimatedIconPartProperty> = [
    "translateX",
    "translateY",
    "opacity",
    "rotate",
    "scaleX",
    "scaleY",
    "dash",
    "blur"
];

let runningLoopCount = 0;

export const getAnimatedIconRange = <Property extends string>({
    keyframes,
    property,
    easing
}: AnimatedIconRangeConstantType<Property>): AnimatedIconRange => {
    const inputRange: Array<number> = [];

    const outputRange: Array<number> = [];

    const easingFunction = ANIMATED_ICON_EASINGS[easing];

    const sampleCount = easing === "linear" ? 1 : ANIMATED_ICON_EASING_SAMPLE_COUNT;

    const frames = keyframes.flatMap((keyframe) => {
        const value = keyframe[property];

        return value === undefined ? [] : [
            {
                offset: keyframe.offset,
                value
            }
        ];
    });

    frames.forEach((frame, index) => {
        const nextFrame = frames[index + 1];

        inputRange.push(frame.offset);

        outputRange.push(frame.value);

        if(!nextFrame) {
            return;
        }

        for(let sample = 1; sample < sampleCount; sample += 1) {
            const ratio = sample / sampleCount;

            inputRange.push(frame.offset + (nextFrame.offset - frame.offset) * ratio);

            outputRange.push(frame.value + (nextFrame.value - frame.value) * easingFunction(ratio));
        }
    });

    return {
        outputRange,
        inputRange
    };
};

export const registerAnimatedIconLoop = (): (() => void) => {
    runningLoopCount += 1;

    if(runningLoopCount > 1) {
        warnOnce({
            message: "AnimatedIcon: more than one looping icon is running. Keep at most one loop (spin, pulse or any animation with animationTrigger=\"loop\") moving at a time on a screen."
        });
    }

    let isReleased = false;

    return () => {
        if(isReleased) {
            return;
        }

        isReleased = true;

        runningLoopCount = Math.max(runningLoopCount - 1, 0);
    };
};

export const getAnimatedIconRangeValue = ({
    progress,
    range
}: AnimatedIconRangeValueConstantType): number => {
    const {
        outputRange,
        inputRange
    } = range;

    const last = inputRange.length - 1;

    if(progress <= (inputRange[0] ?? 0)) {
        return outputRange[0] ?? 0;
    }

    if(progress >= (inputRange[last] ?? 0)) {
        return outputRange[last] ?? 0;
    }

    for(let index = last; index > 0; index -= 1) {
        const fromInput = inputRange[index - 1] ?? 0;

        const fromOutput = outputRange[index - 1] ?? 0;

        if(progress < fromInput) {
            continue;
        }

        const span = (inputRange[index] ?? 0) - fromInput;

        const toOutput = outputRange[index] ?? 0;

        if(!span) {
            return toOutput;
        }

        return fromOutput + (toOutput - fromOutput) * (progress - fromInput) / span;
    }

    return outputRange[last] ?? 0;
};

export const getAnimatedIconTime = ({
    durations,
    time
}: {
    durations: NCoreUIKit.Durations;
    time: AnimatedIconTime;
}): number => {
    return durations[time[0]] * time[1];
};

export const getAnimatedIconPartsKind = ({
    animation,
    trigger,
    icon
}: AnimatedIconPartsKindConstantType): AnimatedIconPartsKind | null => {
    if(animation === "draw") {
        return "draw";
    }

    if(animation === "trace") {
        return trigger === "loop" ? "trace" : "draw";
    }

    if(animation === "signature") {
        return ANIMATED_ICON_SIGNATURES[getAnimatedIconName({
            icon
        })] ? "signature" : "draw";
    }

    return null;
};

export const getAnimatedIconMotionFrames = ({
    row
}: AnimatedIconMotionFramesConstantType): Array<AnimatedIconFrame<AnimatedIconPartProperty>> => {
    const spec = ANIMATED_ICON_MOTIONS[row.motion];

    const {
        sx,
        sy,
        s,
        x,
        y,
        r
    } = {
        ...spec.defaults,
        ...row
    };

    return spec.frames.map((frame) => {
        const next: AnimatedIconFrame<AnimatedIconPartProperty> = {
            offset: frame.offset
        };

        const kx = frame.kx ?? frame.k;

        const ky = frame.ky ?? frame.k;

        if(x !== undefined && kx !== undefined) {
            next.translateX = x * kx;
        }

        if(y !== undefined && ky !== undefined) {
            next.translateY = y * ky;
        }

        if(r !== undefined && frame.k !== undefined) {
            next.rotate = r * frame.k;
        }

        if(frame.q !== undefined || frame.u !== undefined) {
            const q = frame.q ?? 0;

            const u = frame.u ?? 1;

            const scaleX = sx ?? s;

            const scaleY = sy ?? s;

            if(scaleX !== undefined || frame.u !== undefined) {
                next.scaleX = (1 + ((scaleX ?? 1) - 1) * q) * u;
            }

            if(scaleY !== undefined || frame.u !== undefined) {
                next.scaleY = (1 + ((scaleY ?? 1) - 1) * q) * u;
            }
        }

        if(frame.o !== undefined) {
            next.opacity = frame.o;
        }

        if(frame.d !== undefined) {
            next.dash = frame.d;
        }

        return next;
    });
};

export const getAnimatedIconPartFrames = ({
    duration,
    easing,
    frames,
    total,
    delay
}: AnimatedIconPartFramesConstantType): AnimatedIconPartRangesConstantType => {
    return {
        frames: frames.map((frame) => ({
            ...frame,
            offset: (delay + frame.offset * duration) / total
        })),
        easing
    };
};

export const getAnimatedIconPartRanges = ({
    frames,
    easing
}: AnimatedIconPartRangesConstantType): AnimatedIconPartRanges => {
    const ranges: AnimatedIconPartRanges = {};

    PART_PROPERTIES.forEach((property) => {
        const range = getAnimatedIconRange({
            keyframes: frames,
            property,
            easing
        });

        if(range.inputRange.length) {
            ranges[property] = range;
        }
    });

    return ranges;
};

const getPart = ({
    isStrokeConstant,
    easing,
    frames,
    origin
}: AnimatedIconPartRangesConstantType & {
    isStrokeConstant?: boolean;
    origin: [number, number];
}): AnimatedIconPart => {
    return {
        isDashed: frames.some((frame) => frame.dash !== undefined),
        ranges: getAnimatedIconPartRanges({
            frames,
            easing
        }),
        isStrokeConstant,
        origin
    };
};

export const getAnimatedIconPlan = ({
    durations,
    nodes,
    kind,
    icon
}: AnimatedIconPlanConstantType): AnimatedIconPlan => {
    const parts: Array<AnimatedIconPart | null> = nodes.map(() => null);

    if(kind === "trace") {
        const total = getAnimatedIconTime({
            time: ANIMATED_ICON_TRACE_DURATION,
            durations
        });

        const stagger = Math.min(getAnimatedIconTime({
            time: ANIMATED_ICON_DRAW_STAGGER,
            durations
        }), total * ANIMATED_ICON_TRACE_STAGGER_SHARE / Math.max(nodes.length - 1, 1));

        const usable = total - stagger * (nodes.length - 1);

        const at = (time: number) => Math.min(time / total, 1);

        nodes.forEach((_, index) => {
            const start = index * stagger;

            const end = start + usable;

            parts[index] = getPart({
                frames: [
                    {
                        opacity: 0,
                        offset: 0,
                        dash: 1
                    },
                    {
                        offset: at(start),
                        opacity: 0,
                        dash: 1
                    },
                    {
                        offset: Math.min(at(start) + ANIMATED_ICON_STEP_OFFSET, 1),
                        opacity: 1,
                        dash: 1
                    },
                    {
                        offset: at(start + usable * ANIMATED_ICON_TRACE_PHASES.draw),
                        dash: 0
                    },
                    {
                        offset: at(start + usable * (ANIMATED_ICON_TRACE_PHASES.draw + ANIMATED_ICON_TRACE_PHASES.hold)),
                        dash: 0
                    },
                    {
                        offset: at(end),
                        opacity: 1,
                        dash: -1
                    },
                    {
                        offset: Math.min(at(end) + ANIMATED_ICON_STEP_OFFSET, 1),
                        opacity: 0
                    },
                    {
                        opacity: 0,
                        offset: 1,
                        dash: -1
                    }
                ],
                origin: ANIMATED_ICON_CENTER,
                easing: "inOut"
            });
        });

        return {
            isContinuous: false,
            duration: total,
            parts,
            kind
        };
    }

    const entries: Array<{
        frames: Array<AnimatedIconFrame<AnimatedIconPartProperty>>;
        easing: AnimatedIconPartRangesConstantType["easing"];
        origin: [number, number];
        duration: number;
        index: number;
        delay: number;
    }> = [];

    if(kind === "draw") {
        const duration = getAnimatedIconTime({
            time: ANIMATED_ICON_DRAW_DURATION,
            durations
        });

        const stagger = getAnimatedIconTime({
            time: ANIMATED_ICON_DRAW_STAGGER,
            durations
        });

        nodes.forEach((_, index) => {
            entries.push({
                frames: getAnimatedIconMotionFrames({
                    row: {
                        motion: "draw",
                        part: index
                    }
                }),
                origin: ANIMATED_ICON_CENTER,
                delay: index * stagger,
                easing: "out",
                duration,
                index
            });
        });
    } else {
        const rows = ANIMATED_ICON_SIGNATURES[getAnimatedIconName({
            icon
        })] ?? [];

        rows.forEach((row) => {
            const indexes = Array.isArray(row.part) ? row.part : [row.part];

            indexes.forEach((index) => {
                if(index >= nodes.length) {
                    return;
                }

                entries.push({
                    easing: row.easing ?? ANIMATED_ICON_MOTIONS[row.motion].easing ?? "inOut",
                    duration: getAnimatedIconTime({
                        time: row.duration ?? ANIMATED_ICON_SIGNATURE_DURATION,
                        durations
                    }),
                    origin: row.origin ?? ANIMATED_ICON_CENTER,
                    delay: row.delay ? getAnimatedIconTime({
                        time: row.delay,
                        durations
                    }) : 0,
                    frames: getAnimatedIconMotionFrames({
                        row
                    }),
                    index
                });
            });
        });
    }

    const total = Math.max(...entries.map((entry) => entry.delay + entry.duration), 0);

    entries.forEach((entry) => {
        parts[entry.index] = getPart({
            ...getAnimatedIconPartFrames({
                duration: entry.duration,
                easing: entry.easing,
                frames: entry.frames,
                delay: entry.delay,
                total
            }),
            origin: entry.origin
        });
    });

    return {
        isContinuous: false,
        duration: total,
        parts,
        kind
    };
};

export const getAnimatedIconTransitionFrames = ({
    row
}: AnimatedIconTransitionFramesConstantType): Array<AnimatedIconFrame<AnimatedIconPartProperty>> => {
    if(row.motion === "draw") {
        const frames = getAnimatedIconMotionFrames({
            row: {
                motion: "draw",
                part: 0
            }
        });

        return row.isReversed ? frames.map((frame) => frame.dash === undefined ? frame : {
            ...frame,
            dash: -frame.dash
        }) : frames;
    }

    const properties: Array<[AnimatedIconPartProperty, number | undefined, number]> = [
        [
            "translateX",
            row.x,
            0
        ],
        [
            "translateY",
            row.y,
            0
        ],
        [
            "rotate",
            row.r,
            0
        ],
        [
            "scaleX",
            row.sx ?? row.s,
            1
        ],
        [
            "scaleY",
            row.sy ?? row.s,
            1
        ],
        [
            "opacity",
            row.op,
            1
        ],
        [
            "dash",
            row.f === undefined ? undefined : 1 - row.f,
            0
        ],
        [
            "blur",
            row.b,
            0
        ]
    ];

    const isGo = row.motion === "go";

    const start: AnimatedIconFrame<AnimatedIconPartProperty> = {
        offset: 0
    };

    const end: AnimatedIconFrame<AnimatedIconPartProperty> = {
        offset: 1
    };

    properties.forEach(([
        property,
        target,
        rest
    ]) => {
        if(target === undefined) {
            return;
        }

        start[property] = isGo ? rest : target;

        end[property] = isGo ? target : rest;
    });

    return [
        start,
        end
    ];
};

const getAnimatedIconTransitionIndexes = ({
    nodeCount,
    part
}: {
    part: AnimatedIconTransitionPart;
    nodeCount: number;
}): Array<number> => {
    if(part === "all") {
        return Array.from({
            length: nodeCount
        }, (_, index) => index);
    }

    return Array.isArray(part) ? part : [part];
};

const getAnimatedIconTransitionTiming = ({
    durations,
    row
}: AnimatedIconTransitionTimingConstantType): AnimatedIconTransitionTiming => {
    return {
        duration: getAnimatedIconTime({
            time: row.duration ?? ANIMATED_ICON_TRANSITION_DURATION,
            durations
        }),
        delay: row.delay ? getAnimatedIconTime({
            time: row.delay,
            durations
        }) : 0
    };
};

const getAnimatedIconNodeKey = (node: AnimatedIconNode): string => {
    return [
        node.tag,
        ...Object.keys(node.attributes).sort((a, b) => a.localeCompare(b)).map((key) => `${key}=${node.attributes[key]}`)
    ].join(" ");
};

const getAnimatedIconSharedIndexes = ({
    fromNodes,
    toNodes
}: AnimatedIconSharedIndexesConstantType): AnimatedIconSharedIndexes => {
    const toKeys = toNodes.map(getAnimatedIconNodeKey);

    const indexes: AnimatedIconSharedIndexes = {
        from: [],
        to: []
    };

    fromNodes.forEach((node, fromIndex) => {
        const key = getAnimatedIconNodeKey(node);

        const toIndex = toKeys.findIndex((toKey, index) => toKey === key && !indexes.to.includes(index));

        if(toIndex < 0) {
            return;
        }

        indexes.from.push(fromIndex);

        indexes.to.push(toIndex);
    });

    return indexes;
};

const getAnimatedIconTransitionParts = ({
    isCutinEnabled,
    durations,
    nodes,
    total,
    rows
}: AnimatedIconTransitionPartsConstantType): Array<AnimatedIconPart | null> => {
    const parts: Array<AnimatedIconPart | null> = nodes.map(() => null);

    rows.forEach((row) => {
        const {
            duration,
            delay
        } = getAnimatedIconTransitionTiming({
            durations,
            row
        });

        const frames = getAnimatedIconTransitionFrames({
            row
        });

        getAnimatedIconTransitionIndexes({
            nodeCount: nodes.length,
            part: row.part
        }).forEach((index) => {
            if(index >= nodes.length) {
                return;
            }

            parts[index] = getPart({
                ...getAnimatedIconPartFrames({
                    easing: row.easing ?? "inOut",
                    duration,
                    frames,
                    total,
                    delay
                }),
                origin: row.origin ?? ANIMATED_ICON_CENTER,
                isStrokeConstant: true
            });
        });
    });

    if(!isCutinEnabled) {
        return parts;
    }

    return parts.map((part) => part ?? {
        ranges: {
            opacity: {
                inputRange: [
                    0,
                    1 - ANIMATED_ICON_STEP_OFFSET,
                    1
                ],
                outputRange: [
                    0,
                    0,
                    1
                ]
            }
        },
        origin: ANIMATED_ICON_CENTER,
        isDashed: false
    });
};

export const getAnimatedIconTransitionPlan = ({
    transition,
    durations,
    fromNodes,
    isReduced,
    fromIcon,
    toNodes,
    toIcon
}: AnimatedIconTransitionPlanConstantType): AnimatedIconTransitionPlan | null => {
    if(transition === "none") {
        return null;
    }

    const fromName = getAnimatedIconName({
        icon: fromIcon
    });

    const toName = getAnimatedIconName({
        icon: toIcon
    });

    const isMorph = transition === "morph";

    const forward = isMorph && !isReduced ? ANIMATED_ICON_TRANSITIONS[`${fromName}>${toName}`] : undefined;

    const backward = isMorph && !isReduced && !forward ? ANIMATED_ICON_TRANSITIONS[`${toName}>${fromName}`] : undefined;

    const definition = isReduced ? ANIMATED_ICON_REDUCED_TRANSITION : forward ?? backward ?? ANIMATED_ICON_SWAP_TRANSITION;

    const isReversed = !isReduced && !forward && !!backward;

    const [
        pairFromNodes,
        pairToNodes
    ] = isReversed ? [
        toNodes,
        fromNodes
    ] : [
        fromNodes,
        toNodes
    ];

    const sharedIndexes = isReduced || definition === ANIMATED_ICON_SWAP_TRANSITION ? getAnimatedIconSharedIndexes({
        fromNodes: pairFromNodes,
        toNodes: pairToNodes
    }) : {
        from: [],
        to: []
    };

    const layerFromNodes = pairFromNodes.filter((_, index) => !sharedIndexes.from.includes(index));

    const layerToNodes = pairToNodes.filter((_, index) => !sharedIndexes.to.includes(index));

    const total = Math.max(definition.total.reduce((sum, time) => sum + getAnimatedIconTime({
        durations,
        time
    }), 0), ...[
        ...definition.from,
        ...definition.to
    ].map((row) => {
        const {
            duration,
            delay
        } = getAnimatedIconTransitionTiming({
            durations,
            row
        });

        return delay + duration;
    }));

    return {
        shared: {
            nodes: pairFromNodes.filter((_, index) => sharedIndexes.from.includes(index)),
            parts: sharedIndexes.from.map(() => null)
        },
        key: `${fromName}>${toName}:${isReduced ? "reduced" : transition}`,
        from: {
            parts: getAnimatedIconTransitionParts({
                isCutinEnabled: false,
                nodes: layerFromNodes,
                rows: definition.from,
                durations,
                total
            }),
            nodes: layerFromNodes
        },
        to: {
            parts: getAnimatedIconTransitionParts({
                isCutinEnabled: true,
                nodes: layerToNodes,
                rows: definition.to,
                durations,
                total
            }),
            nodes: layerToNodes
        },
        start: isReversed ? 1 : 0,
        end: isReversed ? 0 : 1,
        duration: total
    };
};
